import type { MajorSceneOperation } from '../types';
import type { ScribeOperation } from '../types';

export type ScribeThinkingLevel = 'LOW' | 'MEDIUM' | 'HIGH';

/** Maximum logical Scribe calls in one MAJOR chapter build (4 scenes + 4 retries + 1 gap/regen). */
export const MAJOR_LOGICAL_CALL_BUDGET = 9;

/** Client HTTP attempts per logical scene call (1 initial + 1 transport retry). */
export const SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS = 2;

/** Server Gemini upstream attempts per scene HTTP request. */
export const SCENE_SERVER_MAX_UPSTREAM = 1;

/** Standard transport retries for full-chapter operations. */
export const STANDARD_TRANSPORT_MAX_RETRIES = 5;

/** Worst-case upstream Gemini calls for one MAJOR build. */
export const MAJOR_MAX_UPSTREAM_GEMINI_CALLS =
  MAJOR_LOGICAL_CALL_BUDGET * SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS * SCENE_SERVER_MAX_UPSTREAM;

/** @deprecated use SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS */
export const SCENE_TRANSPORT_MAX_RETRIES = SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS;

export function resolveScribeThinkingLevel(operation: ScribeOperation): ScribeThinkingLevel {
  switch (operation) {
    case 'INITIAL_CHAPTER':
    case 'INSPECTOR_REWRITE':
      return 'HIGH';
    case 'MAJOR_SCENE_BUILD':
    case 'MAJOR_SCENE_RETRY':
      return 'MEDIUM';
    case 'MINOR_INSERTION':
    case 'SMALL_GAP_FILL':
      return 'LOW';
    default: {
      const _exhaustive: never = operation;
      return _exhaustive;
    }
  }
}

export function resolveScribeOperationForMajorScene(operation: MajorSceneOperation): ScribeOperation {
  switch (operation) {
    case 'major_scene_build':
      return 'MAJOR_SCENE_BUILD';
    case 'major_scene_retry':
    case 'weakest_scene_regeneration':
      return 'MAJOR_SCENE_RETRY';
    case 'small_gap_fill':
      return 'SMALL_GAP_FILL';
    default: {
      const _exhaustive: never = operation;
      return _exhaustive;
    }
  }
}

export function resolveMajorSceneOperation(params: {
  gapFillWords?: number;
  isRetry?: boolean;
  isWeakestRegeneration?: boolean;
}): MajorSceneOperation {
  if (params.gapFillWords) return 'small_gap_fill';
  if (params.isWeakestRegeneration) return 'weakest_scene_regeneration';
  if (params.isRetry) return 'major_scene_retry';
  return 'major_scene_build';
}

export function resolveScribeTransportMaxRetries(operation: ScribeOperation): number {
  switch (operation) {
    case 'MAJOR_SCENE_BUILD':
    case 'MAJOR_SCENE_RETRY':
    case 'SMALL_GAP_FILL':
      return SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS;
  }
  return STANDARD_TRANSPORT_MAX_RETRIES;
}

export function assertNoHighThinking(level: ScribeThinkingLevel, allowedHigh = false): void {
  if (level === 'HIGH' && allowedHigh) return;
  if (level !== 'LOW' && level !== 'MEDIUM' && level !== 'HIGH') {
    throw new Error(`Unsupported thinking level: ${String(level)}`);
  }
  if (level === 'HIGH' && !allowedHigh) {
    throw new Error('HIGH thinking is only allowed for INITIAL_CHAPTER and INSPECTOR_REWRITE.');
  }
}

export function isHighThinkingOperation(operation: ScribeOperation): boolean {
  return operation === 'INITIAL_CHAPTER' || operation === 'INSPECTOR_REWRITE';
}

export function computeMaxUpstreamGeminiCalls(params: {
  logicalCalls: number;
  clientTransportAttemptsPerCall?: number;
  serverUpstreamPerRequest?: number;
}): number {
  const clientAttempts = params.clientTransportAttemptsPerCall ?? SCENE_CLIENT_TRANSPORT_MAX_ATTEMPTS;
  const serverUpstream = params.serverUpstreamPerRequest ?? SCENE_SERVER_MAX_UPSTREAM;
  return params.logicalCalls * clientAttempts * serverUpstream;
}

export const FOUR_BEAT_AUTHORITATIVE_IDS = ['beat-1', 'beat-2', 'beat-3', 'beat-4'] as const;

export const FOUR_BEAT_MAX_REPAIR_CALLS = 4;
export const FOUR_BEAT_MAX_REPAIR_PER_BEAT = 1;
export const FOUR_BEAT_INITIAL_LOGICAL_CALLS = 1;

/** One initial four-beat call + up to four targeted beat repairs. */
export const FOUR_BEAT_MAX_LOGICAL_CALLS = 5;

/** Client HTTP attempts per logical four-beat call (1 initial + 1 transient retry). */
export const FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS = 2;

/** Server Gemini upstream calls per incoming four-beat HTTP request. */
export const FOUR_BEAT_SERVER_MAX_UPSTREAM_PER_REQUEST = 1;

/** Maximum upstream attempts per logical four-beat call. */
export const FOUR_BEAT_MAX_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL =
  FOUR_BEAT_CLIENT_TRANSPORT_MAX_ATTEMPTS * FOUR_BEAT_SERVER_MAX_UPSTREAM_PER_REQUEST;

/** Worst-case upstream Gemini calls: 5 logical × 2 attempts = 10. */
export const FOUR_BEAT_MAX_UPSTREAM_GEMINI_CALLS =
  FOUR_BEAT_MAX_LOGICAL_CALLS * FOUR_BEAT_MAX_UPSTREAM_ATTEMPTS_PER_LOGICAL_CALL;

export function buildLogicalSceneCallId(
  chapterNumber: number,
  sceneId: string,
  sceneAttempt: number,
): string {
  return `ch${chapterNumber}-${sceneId}-attempt${sceneAttempt}`;
}
